import type { GameLocale } from "../i18n/types";
import { getMessages } from "../i18n/messages";

export const generateShareText = (
  guesses: string[],
  won: boolean,
  solution: string,
  locale: GameLocale
) => {
  const m = getMessages(locale);
  const title = won ? m.shareTitleWon : m.shareTitleLost;
  const attempts = won ? guesses.length : "X";
  const maxAttempts = 6;

  let shareText = `${title} ${attempts}/${maxAttempts}\n\n`;

  guesses.forEach((guess) => {
    const statuses = getGuessStatuses(guess, solution);
    const emojiRow = statuses
      .map((status) => {
        switch (status) {
          case "correct":
            return "🟩";
          case "present":
            return "🟨";
          case "absent":
            return "⬜";
          default:
            return "⬜";
        }
      })
      .join("");
    shareText += emojiRow + "\n";
  });

  if (!won) {
    shareText += `\n${m.shareWordWas} ${solution}`;
  }

  return shareText;
};

export const shareResult = async (
  guesses: string[],
  won: boolean,
  solution: string,
  locale: GameLocale
) => {
  const m = getMessages(locale);
  const shareText = generateShareText(guesses, won, solution, locale);

  if (navigator.share) {
    try {
      await navigator.share({
        title: m.shareResultTitle,
        text: shareText,
      });
    } catch {
      copyToClipboard(shareText);
    }
  } else {
    copyToClipboard(shareText);
  }
};

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    return true;
  }
};

const getGuessStatuses = (guess: string, solution: string) => {
  const splitSolution = solution.split("");
  const splitGuess = guess.split("");
  const solutionCharsTaken = splitSolution.map(() => false);
  const statuses: ("correct" | "present" | "absent")[] = Array(guess.length).fill(
    "absent"
  );

  splitGuess.forEach((letter, i) => {
    if (letter === splitSolution[i]) {
      statuses[i] = "correct";
      solutionCharsTaken[i] = true;
    }
  });

  splitGuess.forEach((letter, i) => {
    if (statuses[i] === "correct") return;

    const indexOfPresentChar = splitSolution.findIndex(
      (x, index) => x === letter && !solutionCharsTaken[index]
    );

    if (indexOfPresentChar > -1) {
      statuses[i] = "present";
      solutionCharsTaken[indexOfPresentChar] = true;
    }
  });

  return statuses;
};
